import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { BRAND } from "@/lib/brand";
import { Phone, MessageCircle, Menu, X, Sparkles } from "lucide-react";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/blog", label: "Blog" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

const AI_EVAL_URL = "https://fact-based-challenge.emergent.host";

export default function SiteHeader() {
  const location = useLocation();
  const [open, setOpen] = useState(false);

  return (
    <>
      {/* AI Claim Evaluation promo strip */}
      <a
        href={AI_EVAL_URL}
        target="_blank"
        rel="noreferrer"
        data-testid="ai-eval-top-bar"
        className="block w-full text-white"
        style={{ background: "linear-gradient(90deg,#0A2540 0%,#0F9D58 100%)" }}
      >
        <div className="max-w-[1280px] mx-auto px-5 md:px-8 py-2 flex items-center justify-center gap-2 text-[12px] md:text-[13px] font-semibold">
          <Sparkles className="w-3.5 h-3.5 text-gold shrink-0" />
          <span className="hidden sm:inline">New ·</span>
          <span>Try our free <span className="text-gold">AI Claim Evaluation</span></span>
          <span className="hidden md:inline">— get an instant assessment of your claim in minutes</span>
          <span className="ml-1 underline decoration-gold/60 underline-offset-2 group-hover:decoration-gold">Open →</span>
        </div>
      </a>

      <header
        data-testid="site-header"
        className="sticky top-0 z-40 glass"
        style={{ borderBottom: "1px solid rgba(226,232,240,0.6)" }}
      >
      <div className="max-w-[1280px] mx-auto px-5 md:px-8 py-3.5 flex items-center justify-between gap-6">
        <Link to="/" data-testid="site-logo-link" className="flex items-center gap-3">
          <img src={BRAND.logoUrl} alt="Accurate Claims" className="h-12 w-12 object-contain" />
          <div className="leading-tight">
            <div className="font-heading font-bold text-[17px] tracking-tight text-navy">
              ACCURATE CLAIMS
            </div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 -mt-0.5">
              Care · Faith · Accuracy
            </div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                location.pathname === n.to ? "bg-navy text-white" : "text-slate-700 hover:text-navy hover:bg-slate-100"
              }`}
            >
              {n.label}
            </Link>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2">
          <a href={BRAND.telUrl} data-testid="header-phone" className="flex items-center gap-2 text-sm font-medium text-navy hover:text-emerald-brand transition-colors">
            <Phone className="w-4 h-4" /> {BRAND.phone}
          </a>
          <a href={BRAND.whatsappUrl} target="_blank" rel="noreferrer" data-testid="header-whatsapp" className="btn-emerald !py-2 !px-4 text-sm">
            <MessageCircle className="w-4 h-4" /> WhatsApp
          </a>
          <Link to="/login" data-testid="header-login" className="text-sm font-medium px-3 text-navy hover:text-emerald-brand">
            Sign in
          </Link>
        </div>

        <button
          onClick={() => setOpen(!open)}
          data-testid="header-menu-toggle"
          className="md:hidden p-2 rounded-full border border-slate-200"
          aria-label="Menu"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-slate-200 bg-white">
          <div className="px-5 py-4 space-y-1">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                className="block px-4 py-3 rounded-xl text-sm font-medium hover:bg-slate-100"
              >
                {n.label}
              </Link>
            ))}
            <div className="pt-2 flex flex-wrap gap-2">
              <a href={AI_EVAL_URL} target="_blank" rel="noreferrer" className="btn-gold !py-2 !px-4 text-sm" data-testid="mobile-ai-eval-btn">
                <Sparkles className="w-4 h-4" /> AI Claim Evaluation
              </a>
              <a href={BRAND.telUrl} className="btn-outline !py-2 !px-4 text-sm" data-testid="mobile-call-btn">
                <Phone className="w-4 h-4" /> Call
              </a>
              <a href={BRAND.whatsappUrl} target="_blank" rel="noreferrer" className="btn-emerald !py-2 !px-4 text-sm" data-testid="mobile-whatsapp-btn">
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
              <Link to="/login" className="btn-primary !py-2 !px-4 text-sm" onClick={() => setOpen(false)} data-testid="mobile-login-btn">
                Sign in
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
    </>
  );
}
